import { collection, addDoc, query, where, getDocs, updateDoc, doc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { format, isPast, isToday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import { emailService } from './emailService';

/**
 * Service pour gérer les notifications
 */
export const notificationService = {
  /**
   * Vérifie les alertes de rendez-vous à afficher
   * Cette fonction est appelée au chargement de l'application
   */
  checkAppointmentAlerts: async (): Promise<void> => {
    if (!auth.currentUser) return;

    try {
      const now = new Date();
      
      // Récupérer les alertes en attente dont la date est passée
      const alertsQuery = query(
        collection(db, 'alerts'),
        where('userId', '==', auth.currentUser.uid),
        where('status', '==', 'pending')
      );
      
      const querySnapshot = await getDocs(alertsQuery);
      
      // Vérifier s'il y a des alertes en retard
      let hasLateAlerts = false;
      
      querySnapshot.forEach(async (alertDoc) => {
        const alertData = alertDoc.data();
        const alertDate = alertData.date.toDate();
        
        // Si la date d'alerte est passée et que l'alerte n'a pas été affichée
        if (alertDate <= now && !alertData.displayed) {
          // Récupérer les informations du rendez-vous si c'est une alerte de type rendez-vous
          if (alertData.type === 'rendez-vous' && alertData.appointmentId) {
            const appointmentDoc = await getDoc(doc(db, 'appointments', alertData.appointmentId));
            
            if (appointmentDoc.exists()) {
              const appointmentData = appointmentDoc.data();
              
              // Afficher une notification
              toast.success(
                `Rappel: Rendez-vous avec ${appointmentData.title} à ${appointmentData.time}`,
                { duration: 10000 }
              );
              
              // Marquer l'alerte comme affichée
              await updateDoc(doc(db, 'alerts', alertDoc.id), {
                displayed: true,
                displayedAt: serverTimestamp()
              });
            }
          } else {
            // Pour les autres types d'alertes
            toast.success(
              `Rappel: ${alertData.description}`,
              { duration: 10000 }
            );
            
            // Marquer l'alerte comme affichée
            await updateDoc(doc(db, 'alerts', alertDoc.id), {
              displayed: true,
              displayedAt: serverTimestamp()
            });
          }
        }
        
        // Vérifier si l'alerte est en retard
        if (isPast(alertDate) && !isToday(alertDate) && alertData.status !== 'completed') {
          // Marquer l'alerte comme en retard si ce n'est pas déjà fait
          if (alertData.status !== 'late') {
            await updateDoc(doc(db, 'alerts', alertDoc.id), {
              status: 'late',
              updatedAt: serverTimestamp()
            });
          }
          
          hasLateAlerts = true;
        }
      });
      
      // Si des alertes sont en retard, envoyer un email de notification
      if (hasLateAlerts && auth.currentUser.email) {
        await emailService.sendLateAppointmentsNotification(auth.currentUser.email);
      }
    } catch (error) {
      console.error('Error checking appointment alerts:', error);
    }
  },

  /**
   * Planifie une notification pour un rendez-vous
   * @param appointment Les données du rendez-vous
   * @param alertDate La date d'affichage de la notification
   */
  scheduleAppointmentNotification: async (appointment: any, alertDate: Date): Promise<void> => {
    if (!auth.currentUser) return;

    try {
      // Créer une alerte pour le rendez-vous
      await addDoc(collection(db, 'alerts'), {
        type: 'rendez-vous',
        company: appointment.title,
        date: alertDate,
        agency: appointment.agency,
        status: 'pending',
        step: 0,
        description: `Rendez-vous avec ${appointment.contact} à ${appointment.time}`,
        action: `Rappel de rendez-vous (${appointment.alertType})`,
        userId: auth.currentUser.uid,
        appointmentId: appointment.id,
        displayed: false,
        emailSent: false,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error scheduling appointment notification:', error);
      throw error;
    }
  },
  
  /**
   * Vérifie et met à jour le statut des alertes (en retard, etc.)
   */
  updateAlertsStatus: async (): Promise<void> => {
    if (!auth.currentUser) return;
    
    try {
      const now = new Date();
      
      // Récupérer toutes les alertes en attente
      const alertsQuery = query(
        collection(db, 'alerts'),
        where('userId', '==', auth.currentUser.uid),
        where('status', '==', 'pending')
      );
      
      const querySnapshot = await getDocs(alertsQuery);
      
      querySnapshot.forEach(async (alertDoc) => {
        const alertData = alertDoc.data();
        const alertDate = alertData.date.toDate();
        
        // Si la date d'alerte est passée et que ce n'est pas aujourd'hui
        if (isPast(alertDate) && !isToday(alertDate)) {
          // Marquer l'alerte comme en retard
          await updateDoc(doc(db, 'alerts', alertDoc.id), {
            status: 'late',
            updatedAt: serverTimestamp()
          });
        }
      });
    } catch (error) {
      console.error('Error updating alerts status:', error);
    }
  }
};